import PropTypes from "prop-types";
import { Grid, Typography } from "@mui/material";
import Box from "@mui/material/Box";
import { Doughnut } from "react-chartjs-2";
import ChartDataLabels from "chartjs-plugin-datalabels";
import {
  ANNUAL_FAILED_COLOR,
  ANNUAL_SUCCESS_COLOR,
  GraphicNoDataImg,
} from "../constants/graphics";
import ChartPlaceholder from "./chartPlaceholder";
import {
  formatNumber,
  formatNumberShort,
} from "../../../helpers/functions/functions";

// Total acumulado anual: dona con la efectividad del año completo.
// El centro muestra el total enviado y cada porción su porcentaje.
const AnnualTotalDonut = ({ darkMode, loading, total }) => {
  const { solicitados, enviados, fallidos } = total;
  const hasData = solicitados > 0;
  const successPercent = hasData ? (enviados * 100) / solicitados : 0;
  const failedPercent = hasData ? (fallidos * 100) / solicitados : 0;

  return (
    <Grid
      item
      container
      direction={"column"}
      sx={{
        width: "100%",
        height: "100%",
        padding: "20px",
        borderRadius: "10px",
        backgroundColor: darkMode ? "#000000DE" : "#FFFFFF",
        border:
          "1px solid " + (darkMode ? "rgba(224, 224, 224, 0.50)" : "#E0E0E0"),
        boxShadow: "0 2px 8px 0 rgba(0, 0, 0, 0.10)",
      }}
    >
      <Typography
        sx={{
          fontFamily: "Ubuntu !important",
          fontWeight: "500",
          fontSize: "18px",
          color: darkMode ? "white" : "black",
        }}
      >
        Total acumulado anual
      </Typography>

      {loading || !hasData ? (
        <ChartPlaceholder
          darkMode={darkMode}
          loading={loading}
          image={
            darkMode
              ? GraphicNoDataImg.NoDataDoughnutDark
              : GraphicNoDataImg.NoDataDoughnutWhite
          }
        />
      ) : (
        <>
          <Grid
            sx={{
              flex: 1,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              padding: "20px 0",
            }}
          >
            <Box
              sx={{
                position: "relative",
                width: "100%",
                maxWidth: "300px",
                aspectRatio: "1 / 1",
              }}
            >
              <Doughnut
                data={{
                  labels: ["sms exitosos", "sms fallidos"],
                  datasets: [
                    {
                      data: [enviados, fallidos],
                      backgroundColor: [
                        ANNUAL_SUCCESS_COLOR,
                        ANNUAL_FAILED_COLOR,
                      ],
                      borderWidth: 0,
                      hoverBorderWidth: 0,
                    },
                  ],
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  // Arranca los fallidos en el tope para replicar el prototipo
                  rotation: -successPercent * 3.6,
                  cutout: "72%",
                  layout: { padding: 30 },
                  plugins: {
                    legend: { display: false },
                    tooltip: {
                      enabled: true,
                      backgroundColor: !darkMode ? "white" : "#4E4E4E",
                      bodyColor: !darkMode ? "#000" : "white",
                      borderColor: "#1BC3DD",
                      borderWidth: 1,
                      titleColor: !darkMode ? "#000" : "white",
                      displayColors: false,
                      callbacks: {
                        label: (context) =>
                          ` ${context.label}: ${formatNumber(context.parsed)}`,
                      },
                    },
                    datalabels: {
                      anchor: "end",
                      align: "end",
                      offset: 4,
                      color: (context) =>
                        context.dataset.backgroundColor[context.dataIndex],
                      font: {
                        family: "Ubuntu",
                        size: 22,
                        weight: "bold",
                      },
                      formatter: (value) =>
                        `${Math.round((value * 100) / solicitados)}%`,
                    },
                  },
                }}
                plugins={[ChartDataLabels]}
              />

              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  pointerEvents: "none",
                  flexDirection: "column",
                }}
              >
                <Typography
                  sx={{
                    fontFamily: "Ubuntu !important",
                    fontWeight: "400",
                    fontSize: "40px",
                    lineHeight: 1,
                    color: darkMode ? "white" : "#000",
                  }}
                >
                  {formatNumberShort(solicitados)}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: "Ubuntu !important",
                    fontWeight: "300",
                    fontSize: "16px",
                    color: darkMode ? "white" : "#000",
                  }}
                >
                  enviados
                </Typography>
              </Box>
            </Box>
          </Grid>

          <Grid
            sx={{
              display: "flex",
              justifyContent: "center",
              gap: "40px",
              flexShrink: 0,
            }}
          >
            {[
              {
                label: "sms exitosos",
                color: ANNUAL_SUCCESS_COLOR,
                percent: successPercent,
              },
              {
                label: "sms fallidos",
                color: ANNUAL_FAILED_COLOR,
                percent: failedPercent,
              },
            ].map(({ label, color, percent }) => (
              <Box
                key={label}
                sx={{ display: "flex", alignItems: "center", gap: "5px" }}
                title={`${percent.toFixed(1)}%`}
              >
                <Box
                  sx={{ width: "11px", height: "11px", backgroundColor: color }}
                />
                <Typography
                  sx={{
                    fontFamily: "Ubuntu !important",
                    fontSize: "12px",
                    color: darkMode ? "white" : "#000",
                  }}
                >
                  {label}
                </Typography>
              </Box>
            ))}
          </Grid>
        </>
      )}
    </Grid>
  );
};

AnnualTotalDonut.propTypes = {
  darkMode: PropTypes.bool.isRequired,
  loading: PropTypes.bool.isRequired,
  total: PropTypes.shape({
    solicitados: PropTypes.number,
    enviados: PropTypes.number,
    fallidos: PropTypes.number,
  }).isRequired,
};

export default AnnualTotalDonut;
